import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import BEM from '../../bem';
import getApiErrorMessage from '../../utils/getApiErrorMessage';
import mobxInjectSelect from '../../utils/mobxInjectSelect';
import validatePbxPhone from '../../utils/validatePbxPhone';
import TCClient from '../../../client';
import { PBXLineDetails } from '../../../types';
import Modal from '../Modal';

const classes = BEM.with('UserLineDetailsModal');

type UserLineDetailsModalProps = {
  options: {
    pbxLineId: string;
    onSuccess?: () => void;
  };
};

type MobxProps = {
  closeModal: () => void;
  currentOrganizationId: string;
  isOpen: boolean;
};

type FormFields = {
  firstName: string;
  lastName: string;
  callerIdNumber: string;
  emergencyCallerIdNumber: string;
  forwardAlwaysEnabled: boolean;
  forwardAlwaysDestination: string;
};

const DEFAULT_VALUES: FormFields = {
  firstName: '',
  lastName: '',
  callerIdNumber: '',
  emergencyCallerIdNumber: '',
  forwardAlwaysEnabled: false,
  forwardAlwaysDestination: '',
};

const initialsFor = (firstName: string, lastName: string) => {
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  return initials || '?';
};

const UserLineDetailsModal = ({
  closeModal,
  currentOrganizationId,
  isOpen,
  options: { pbxLineId, onSuccess },
}: MobxProps & UserLineDetailsModalProps) => {
  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
    reset,
  } = useForm<FormFields>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onSubmit',
  });

  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState('');
  const [lineDetails, setLineDetails] = useState<PBXLineDetails | null>(null);

  const forwardAlwaysEnabled = watch('forwardAlwaysEnabled');

  const extension = lineDetails?.customSettings?.extensionNumber || '';
  const headerName = [lineDetails?.firstName, lineDetails?.lastName].filter(Boolean).join(' ');
  const headerInitials = initialsFor(lineDetails?.firstName || '', lineDetails?.lastName || '');

  useEffect(() => {
    if (!isOpen || !pbxLineId) return undefined;

    let cancelled = false;
    setIsLoading(true);
    setLoadError('');
    setApiError('');
    reset(DEFAULT_VALUES);
    setLineDetails(null);

    (async () => {
      try {
        const details = (await TCClient.pbx.getPbxLineDetails({
          orgId: currentOrganizationId,
          pbxLineId,
          detailed: true,
        })) as PBXLineDetails;
        if (cancelled) return;
        setLineDetails(details);
        reset({
          firstName: details.firstName || '',
          lastName: details.lastName || '',
          callerIdNumber: details.callerIdNumber || '',
          emergencyCallerIdNumber: details.emergencyCallerIdNumber || '',
          forwardAlwaysEnabled: Boolean(details.forwardAlwaysEnabled),
          forwardAlwaysDestination: details.forwardAlwaysDestination || '',
        });
      } catch (err) {
        if (!cancelled) {
          setLoadError('Failed to load line details. Please try again.');
          console.error(err);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen, pbxLineId, currentOrganizationId, reset]);

  const onSubmit = async (formData: FormFields) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setApiError('');

    try {
      await TCClient.pbx.updatePbxLineDetails({
        orgId: currentOrganizationId,
        pbxLineId,
        details: {
          ...formData,
          forwardAlwaysDestination: formData.forwardAlwaysEnabled
            ? formData.forwardAlwaysDestination
            : '',
        },
      });
      onSuccess?.();
      closeModal();
    } catch (err) {
      setApiError(getApiErrorMessage(err, 'Failed to save line details. Please try again.'));
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isBusy = isLoading || isSubmitting;

  return (
    <Modal
      bodyClass={classes('body')}
      closeClass={classes('close-btn')}
      footerClass={classes('footer')}
      header="Edit Line Details"
      headerClass={classes('header')}
      isOpen={isOpen}
      size="large"
      className={classes()}
      onRequestClose={closeModal}
      footerPrimaryActions={
        <button
          aria-label="Save"
          className={classes('primary-btn')}
          type="button"
          onClick={handleSubmit(onSubmit)}
          disabled={isBusy || Boolean(loadError)}
        >
          {isSubmitting ? 'Saving...' : 'Save Line'}
        </button>
      }
      footerSecondaryActions={
        <button
          aria-label="Cancel"
          className={classes('secondary-btn')}
          type="button"
          onClick={closeModal}
        >
          Cancel
        </button>
      }
    >
      {isLoading && <div className={classes('status')}>Loading...</div>}
      {!isLoading && loadError && (
        <div className={classes('status', { error: true })}>{loadError}</div>
      )}
      {!isLoading && !loadError && (
        <>
          <div className={classes('line-header')}>
            <div className={classes('avatar')}>{headerInitials}</div>
            <div className={classes('line-header-text')}>
              <div className={classes('line-header-name')}>{headerName || 'Unassigned'}</div>
              <div className={classes('line-header-extension')}>Extension {extension}</div>
            </div>
          </div>

          <div className={classes('note')}>
            Caller ID Number and Emergency Caller ID are required for all lines. Forward Always
            Destination is required only when Forward Always is enabled.
          </div>

          <div className={classes('form-container')}>
            <div className={classes('form-row')}>
              <div className={classes('form-field')}>
                <label className={classes('label')}>First Name</label>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <input
                      className={classes('input')}
                      type="text"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      placeholder="First name"
                    />
                  )}
                />
              </div>
              <div className={classes('form-field')}>
                <label className={classes('label')}>Last Name</label>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <input
                      className={classes('input')}
                      type="text"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      placeholder="Last name"
                    />
                  )}
                />
              </div>
            </div>

            <div className={classes('form-row')}>
              <div className={classes('form-field')}>
                <label className={classes('label')}>
                  Caller ID Number
                  <span className={classes('required')}>*</span>
                </label>
                <Controller
                  name="callerIdNumber"
                  control={control}
                  rules={{ validate: validatePbxPhone }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <input
                      className={classes('input', { error: Boolean(errors.callerIdNumber) })}
                      type="text"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      placeholder="Enter caller ID number"
                    />
                  )}
                />
                {errors.callerIdNumber && (
                  <span className={classes('error-message')}>{errors.callerIdNumber.message}</span>
                )}
              </div>

              <div className={classes('form-field')}>
                <label className={classes('label')}>
                  Emergency Caller ID
                  <span className={classes('required')}>*</span>
                </label>
                <Controller
                  name="emergencyCallerIdNumber"
                  control={control}
                  rules={{ validate: validatePbxPhone }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <input
                      className={classes('input', {
                        error: Boolean(errors.emergencyCallerIdNumber),
                      })}
                      type="text"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      placeholder="Enter emergency caller ID"
                    />
                  )}
                />
                {errors.emergencyCallerIdNumber && (
                  <span className={classes('error-message')}>
                    {errors.emergencyCallerIdNumber.message}
                  </span>
                )}
              </div>
            </div>

            <div className={classes('form-row')}>
              <div className={classes('form-field')}>
                <label className={classes('label')}>Forward Always Enabled</label>
                <Controller
                  name="forwardAlwaysEnabled"
                  control={control}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <select
                      className={classes('select')}
                      value={value ? 'Yes' : 'No'}
                      onChange={(e) => onChange(e.target.value === 'Yes')}
                      onBlur={onBlur}
                    >
                      <option>No</option>
                      <option>Yes</option>
                    </select>
                  )}
                />
              </div>
              <div className={classes('form-field')}>
                <label className={classes('label')}>
                  Forward Always Destination
                  {forwardAlwaysEnabled && <span className={classes('required')}>*</span>}
                </label>
                <Controller
                  name="forwardAlwaysDestination"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (!forwardAlwaysEnabled) return true;
                      return value?.trim().length > 0 || 'This field is required.';
                    },
                  }}
                  render={({ field: { onChange, onBlur, value } }) => (
                    <input
                      className={classes('input', {
                        error: Boolean(errors.forwardAlwaysDestination),
                      })}
                      type="text"
                      value={value}
                      onChange={onChange}
                      onBlur={onBlur}
                      disabled={!forwardAlwaysEnabled}
                      placeholder="—"
                    />
                  )}
                />
                {errors.forwardAlwaysDestination && (
                  <span className={classes('error-message')}>
                    {errors.forwardAlwaysDestination.message}
                  </span>
                )}
              </div>
            </div>

            {apiError && <div className={classes('error-message')}>{apiError}</div>}
          </div>
        </>
      )}
    </Modal>
  );
};

export default mobxInjectSelect<UserLineDetailsModalProps, MobxProps>({
  messengerStore: ['currentOrganizationId'],
})(UserLineDetailsModal);
